<template>
    <MLoading v-if="showLoading" />
    <MContainer class="w-full mt-4 items-center justify-center upload-container">
        <div class="w-full">
            <UniversalFilePicker
                class="grow"
                :dropArea="true"
                :selectFilesBtn="true"
                :selectFolderBtn="false"
                :accept="imgExtendedFormats"
                :context="processingContext ? (processingContext as DocumentContext) : undefined"
                :writeToWasmFS="false"
                :filterAssociatedFiles="false"
                objectName="test2"
                :singleSelection="true"
                :embedded="true"
                :showTypeColumn="false"
                :hideTreeWhenNoFolders="true"
                :showAccept="true"
                @uploadFiles="onUploadFiles" />
        </div>
        <MContainer v-if="iuState === UIState.UPLOADING" class="upload-overlay items-center justify-center">
            <ProgressSpinner style="width: 150px; height: 150px" animationDuration=".75s" />
        </MContainer>
    </MContainer>
</template>
<script lang="ts" setup>
import { MLoading, MContainer, imgExtendedFormats, useAssets, exception, Exception, success } from "@xlabmedical/medic-web-framework";
import { ProjectFileType } from "@xlabmedical/medic-api";
import { UniversalFilePicker, DocumentContext, useWasmEngine, IEngineGenerated } from "@xlabmedical/medic-web-engine";
import { CustomFile, usePatientsStore } from "@/stores/PatientsStore";
import { useI18n } from "vue-i18n";

const assetApi = useAssets();

enum UIState {
    INIT,
    SELECTED_OBJECT,
    UPLOADING,
}

const patientData = defineModel<{
    firstName: string | undefined;
    lastName: string;
    notes: string | undefined;
    dateOfBirth: string;
    extra: {
        patientName: string | undefined;
        study_instance_UID: string;
    };
}>("patientData", { required: false });
const props = defineProps<{
    patientName: string;
    createPatient: () => Promise<void>;
}>();

const emits = defineEmits<{ (e: "done", value: string): void }>();

const iuState = ref<UIState>(UIState.INIT);

const patientStore = usePatientsStore();
const { t } = useI18n();

const files = ref<File[]>([]);
const showLoading = ref(false);
const processingContext = ref(new DocumentContext(assetApi.getAssetUrl("")));
const isEngineReady = ref(false);
let module: any;
let engineInstance: IEngineGenerated;
const serverApi = useServerApi();

const DEFAULT_SVG_SIZE = 1024;
const MAX_SVG_SIZE = 1024;

const initEngine = async () => {
    if (isEngineReady.value) {
        return true;
    }

    try {
        module = await useWasmEngine();
        if (!module) {
            console.error("WASM module is null");
            return false;
        }
        engineInstance = new IEngineGenerated(module as any);
        processingContext.value.module = module;
        processingContext.value.setExternalEngineInstance(engineInstance);
        processingContext.value.xWasmDocument = new engineInstance.xWasmDocumentJS();
        processingContext.value.makeDir("/app");
        isEngineReady.value = true;
        return true;
    } catch (err) {
        console.error("Failed to initialize WASM engine", err);
        return false;
    }
};

onMounted(async () => {
    try {
        await initEngine();
    } catch (err) {
        exception("Error while initializing engine: ", err as Exception);
    }
});

const uploadProgress = ref(0);
const throttleUpdate = useThrottleFn((progress: number) => {
    uploadProgress.value = Math.ceil(progress * 100) - 1;
}, 250);

const isSvgFile = (file: File) => file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

const parseSvgLength = (value: string | null) => {
    if (!value) return null;
    if (value.trim().endsWith("%")) return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getSvgDimensions = (svgText: string) => {
    try {
        const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
        const svg = doc.documentElement;
        const widthAttr = parseSvgLength(svg.getAttribute("width"));
        const heightAttr = parseSvgLength(svg.getAttribute("height"));
        if (widthAttr && heightAttr) {
            return { width: widthAttr, height: heightAttr };
        }
        const viewBox = svg.getAttribute("viewBox");
        if (viewBox) {
            const parts = viewBox
                .trim()
                .split(/[\s,]+/)
                .map((val) => parseFloat(val));
            if (parts.length === 4 && parts.every((val) => Number.isFinite(val))) {
                return { width: parts[2], height: parts[3] };
            }
        }
    } catch (err) {
        console.warn("Failed to parse SVG dimensions", err);
    }
    return { width: DEFAULT_SVG_SIZE, height: DEFAULT_SVG_SIZE };
};

const loadSvgImage = (svgText: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };
        img.src = url;
    });

const canvasToPngBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png");
    });

const createSvgScreenshot = async (file: File) => {
    try {
        const svgText = await file.text();
        const svgDims = getSvgDimensions(svgText);
        const image = await loadSvgImage(svgText);
        const rawWidth = image.naturalWidth || svgDims.width || DEFAULT_SVG_SIZE;
        const rawHeight = image.naturalHeight || svgDims.height || DEFAULT_SVG_SIZE;
        const scale = Math.min(1, MAX_SVG_SIZE / Math.max(rawWidth, rawHeight));
        const canvasWidth = Math.max(1, Math.round(rawWidth * scale));
        const canvasHeight = Math.max(1, Math.round(rawHeight * scale));

        const canvas = document.createElement("canvas");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
        return await canvasToPngBlob(canvas);
    } catch (err) {
        console.warn("Failed to create SVG screenshot", err);
        return null;
    }
};

const uploadSvgScreenshot = async (file: File) => {
    const screenshot = await createSvgScreenshot(file);
    if (!screenshot) return;
    await serverApi.uploadFile(patientData.value ? patientData.value.extra.study_instance_UID : props.patientName, file.name + ".thumb", ProjectFileType.SVG_Preview, screenshot);
};

const importImage = async () => {
    showLoading.value = true;
    iuState.value = UIState.UPLOADING;

    if (patientData.value != undefined) {
        if (patientData.value.extra.study_instance_UID == "") {
            await props.createPatient();
        }
    }
    await Promise.all(
        files.value.map(async (file: File) => {
            if (isSvgFile(file)) {
                await uploadSvgScreenshot(file);
            }

            await patientStore.uploadFile(
                patientData.value ? patientData.value.extra.study_instance_UID : props.patientName,
                file.name,
                ProjectFileType.Image,
                file as CustomFile,
                (progress: number) => {
                    throttleUpdate(progress);
                },
            );
        }),
    );

    success(files.value.length > 1 ? t("web-rg.imagesUploadedSuccessfully") : t("web-rg.imageUploadedSuccessfully"));
    removeFiles();
    showLoading.value = false;
    iuState.value = UIState.INIT;
    emits("done", patientData.value ? patientData.value.extra.study_instance_UID : props.patientName);
};

const onUploadFiles = async (selection: File[]) => {
    iuState.value = UIState.SELECTED_OBJECT;
    files.value = selection;
    await importImage();
};

const removeFiles = () => {
    files.value = [];
    iuState.value = UIState.INIT;
};
</script>
<style scoped>
.upload-container {
    position: relative;
}

.upload-overlay {
    position: absolute;
    inset: 0;
    background: rgba(100, 100, 100, 0.4);
    z-index: 2;
    border-radius: 16px;
}
</style>
